import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Building2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function SocietyJoin() {
    const { code } = useParams();
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();
    const [soc, setSoc] = useState(null);
    const [join, setJoin] = useState(null);
    const fired = useRef(false);

    useEffect(() => {
        localStorage.setItem("homiie_society", code.toUpperCase());
        api.get(`/public/society/${code}`).then((r) => setSoc(r.data)).catch(() => setSoc(false));
    }, [code]);

    useEffect(() => {
        if (!soc || loading || user?.role !== "customer" || fired.current) { return; }
        fired.current = true;
        const fresh = sessionStorage.getItem("homiie_just_joined") === code.toUpperCase();
        sessionStorage.removeItem("homiie_just_joined");
        api.post("/society/join", { code }).then((r) => setJoin({ ok: true, ...r.data, joined: r.data.joined || fresh })).catch((e) => setJoin({ ok: false, error: apiError(e.response?.data?.detail) }));
    }, [soc, user, loading, code]);

    if (soc === null || loading) return <Center><Loader2 className="h-7 w-7 animate-spin text-orange" /></Center>;
    if (soc === false) return <Center><p data-testid="society-join-not-found" className="text-sm text-navy/60">This society link is no longer active.</p><Link to="/" className="mt-3 text-sm font-bold text-orange">Go to HOMIIE</Link></Center>;

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center bg-lightbg px-6 py-10">
            <Logo size={46} />
            <div className="mt-6 rounded-3xl bg-navy p-5 text-white" data-testid="society-join-card">
                <Building2 className="text-orange" size={26} />
                <p className="mt-3 text-xs uppercase tracking-wider text-white/50">Resident of</p>
                <h1 className="font-heading text-2xl font-extrabold" data-testid="society-join-name">{soc.name}</h1>
                {soc.area_name && <p className="text-sm text-white/60">{soc.area_name}</p>}
                {soc.deal_discount && <p className="mt-3 rounded-xl bg-white/10 px-3 py-2 text-sm font-semibold text-orange" data-testid="society-join-offer">{soc.deal_discount}</p>}
            </div>
            {!user && <Guest code={code} />}
            {user && user.role !== "customer" && (
                <div className="mt-5 rounded-2xl border border-navy/10 bg-white p-4 text-sm text-navy" data-testid="society-join-wrong-role">
                    <p>You're signed in as a {user.role}. Log out and use a customer account to join.</p>
                    <button data-testid="society-join-logout" onClick={logout} className="mt-3 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold">Log out</button>
                </div>
            )}
            {user?.role === "customer" && !join && <Center><Loader2 className="h-6 w-6 animate-spin text-orange" /></Center>}
            {join?.ok && (
                <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="society-join-success">
                    <p className="flex items-center gap-2 font-heading font-bold text-emerald-800"><CheckCircle2 size={18} /> {join.joined ? "You've joined" : "You're already a member of"} {soc.name}</p>
                    <p className="mt-1 text-xs text-emerald-800/70">Bookings you make are now tagged to your society.</p>
                    <button data-testid="society-join-book" onClick={() => nav(`/book?society=${soc.code}`)} className="mt-3 w-full rounded-full bg-orange py-3 text-sm font-bold text-white">Book a service</button>
                    <button data-testid="society-join-home" onClick={() => nav("/")} className="mt-2 w-full rounded-full border border-navy/15 py-2.5 text-xs font-bold text-navy">Go to home</button>
                </div>
            )}
            {join && !join.ok && (
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="society-join-error">
                    <p className="flex items-center gap-2 font-bold"><AlertTriangle size={16} /> {join.error}</p>
                    <button onClick={() => nav("/")} className="mt-3 rounded-full border border-amber-300 px-4 py-2 text-xs font-bold">Go to home</button>
                </div>
            )}
        </div>
    );
}

function Guest({ code }) {
    return (
        <div className="mt-5 space-y-2.5" data-testid="society-join-guest">
            <p className="text-sm text-navy/60">Join HOMIIE as a resident to get trusted home services for your society.</p>
            <Link to={`/register?society=${code}`} data-testid="society-join-signup" className="block w-full rounded-full bg-orange py-3.5 text-center text-sm font-bold text-white shadow-lg shadow-orange/30">Sign up</Link>
            <Link to="/login" state={{ from: `/society/${code}` }} data-testid="society-join-login" className="block w-full rounded-full border border-navy/15 bg-white py-3.5 text-center text-sm font-bold text-navy">I already have an account · Log in</Link>
        </div>
    );
}

function Center({ children }) {
    return <div className="flex min-h-[30vh] flex-col items-center justify-center">{children}</div>;
}
